import React, { useEffect, useMemo, useState } from 'react';
import { DataTable } from '../components/DataTable';
import { LoadingBlock } from '../components/LoadingBlock';
import { SectionCard } from '../components/SectionCard';

const EMPTY_FORM = {
  id: null,
  category_id: '1',
  team_id: '',
  name: '',
  sku: '',
  price: '',
  stock: '0',
  status: 'active',
  description: '',
  cover_image_url: '',
};

export function ProductsPage({
  loading,
  error,
  data,
  onCreateProduct,
  onUpdateProduct,
  onDeleteProduct,
  onUploadProductImage,
  onConnectMercadoPago,
  onFilter,
  creating,
  updating,
  uploading,
  deleting,
  connectingMarketplace,
  canManage,
}) {
  const products = data?.items || [];
  const teams = data?.teams || [];
  const categories = data?.categories || [];
  const filters = data?.filters || { status: 'all', team_id: '', category_id: '' };
  const marketplace = data?.marketplace || null;
  const paymentAccounts = marketplace?.accounts || [];
  const [form, setForm] = useState(EMPTY_FORM);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageMessage, setImageMessage] = useState('');

  const summary = useMemo(() => ({
    total: products.length,
    active: products.filter((item) => item.status === 'active').length,
    lowStock: products.filter((item) => Number(item.stock) <= 5).length,
  }), [products]);

  useEffect(() => {
    if (!categories.length && form.category_id === '1') return;
    if (categories.length && !categories.some((category) => String(category.id) === String(form.category_id))) {
      setForm((current) => ({ ...current, category_id: String(categories[0].id) }));
    }
  }, [categories, form.category_id]);

  const submit = async (event) => {
    event.preventDefault();
    const payload = {
      category_id: Number(form.category_id),
      team_id: form.team_id ? Number(form.team_id) : null,
      name: form.name,
      sku: form.sku,
      price: Number(form.price),
      stock: Number(form.stock),
      status: form.status,
      description: form.description,
      cover_image_url: form.cover_image_url || null,
    };

    if (form.id) {
      await onUpdateProduct(form.id, payload);
    } else {
      await onCreateProduct(payload);
    }
    setForm({
      ...EMPTY_FORM,
      category_id: categories[0] ? String(categories[0].id) : '1',
    });
    setSelectedImage(null);
    setImageMessage('');
  };

  const uploadSelectedImage = async () => {
    if (!selectedImage || !onUploadProductImage) return;
    const url = await onUploadProductImage(selectedImage, form.id);
    if (url) {
      setForm((current) => ({ ...current, cover_image_url: url }));
      setSelectedImage(null);
      setImageMessage('Imagem enviada. Salve o produto para aplicar na loja.');
    }
  };

  const resetForm = () => {
    setForm({ ...EMPTY_FORM, category_id: categories[0] ? String(categories[0].id) : '1' });
    setSelectedImage(null);
    setImageMessage('');
  };

  if (loading) return <LoadingBlock label="Carregando catálogo..." />;
  if (error) return <div className="page-error">{error}</div>;

  return (
    <div className="page-grid two-columns">
      <SectionCard
        title="Catálogo"
        subtitle={`${summary.total} itens • ${summary.active} ativos • ${summary.lowStock} com estoque baixo`}
        actions={canManage ? (
          <button
            className="ghost-button"
            type="button"
            onClick={resetForm}
          >
            Novo produto
          </button>
        ) : null}
      >
        {canManage ? (
          <div className="callout-box compact-box">
            <span className="eyebrow">Acesso do administrador</span>
            <strong>Gestão total da loja</strong>
            <p>Como admin, você adiciona, edita e exclui produtos, controla vínculo com time, estoque, preço, imagem e status da vitrine.</p>
          </div>
        ) : null}
        <div className="toolbar-row">
          <select value={filters.status || 'all'} onChange={(e) => onFilter({ ...filters, status: e.target.value })}>
            <option value="all">Todos os status</option>
            <option value="active">Ativos</option>
            <option value="inactive">Inativos</option>
            <option value="draft">Rascunho</option>
          </select>
          <select value={filters.team_id || ''} onChange={(e) => onFilter({ ...filters, team_id: e.target.value })}>
            <option value="">Todos os times</option>
            {teams.map((team) => (
              <option key={team.id} value={team.id}>{team.name}</option>
            ))}
          </select>
          <select value={filters.category_id || ''} onChange={(e) => onFilter({ ...filters, category_id: e.target.value })}>
            <option value="">Todas as categorias</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>{category.name}</option>
            ))}
          </select>
        </div>

        <DataTable
          rows={products}
          columns={[
            { key: 'name', label: 'Produto' },
            { key: 'team_name', label: 'Time' },
            { key: 'category_name', label: 'Categoria' },
            { key: 'price', label: 'Preço' },
            { key: 'stock', label: 'Estoque' },
            { key: 'status', label: 'Status', type: 'status' },
            {
              key: 'actions',
              label: 'Ação',
              render: (row) => (
                <div className="inline-actions">
                  <button className="inline-button" type="button" onClick={() => setForm({
                    id: row.id,
                    category_id: String(row.category_id),
                    team_id: row.team_id ? String(row.team_id) : '',
                    name: row.name,
                    sku: row.sku,
                    price: String(row.price),
                    stock: String(row.stock),
                    status: row.status,
                    description: row.description || '',
                    cover_image_url: row.cover_image_url || '',
                  }) || (setSelectedImage(null), setImageMessage(''))}>Editar</button>
                  {canManage ? <button className="inline-button danger-text" type="button" onClick={() => onDeleteProduct(row.id)}>Excluir</button> : null}
                </div>
              ),
            },
          ]}
        />
      </SectionCard>

      <SectionCard
        title="Mercado Pago"
        subtitle={marketplace?.configured ? `Comissão Perifa: ${Number(marketplace.marketplace_fee_percent || 0).toFixed(1)}% por venda` : 'Configure as variáveis Mercado Pago no servidor para ativar split.'}
      >
        {canManage ? (
          <>
            <div className="callout-box compact-box">
              <span className="eyebrow">Split por time</span>
              <strong>O dinheiro da venda vai para a conta Mercado Pago do time.</strong>
              <p>O Perifa recebe a comissão configurada; o vendedor conecta a própria conta e mantém seus dados financeiros fora do painel.</p>
            </div>
            {teams.length ? (
              <div className="stack-list">
                {teams.map((team) => {
                  const account = paymentAccounts.find((item) => Number(item.team_id) === Number(team.id));
                  const connected = account?.status === 'active';
                  return (
                    <div className="stack-list__item" key={team.id}>
                      <div>
                        <strong>{team.name}</strong>
                        <p>{connected ? `Conectado${account.connected_at ? ` em ${account.connected_at}` : ''}` : 'Mercado Pago ainda não conectado.'}</p>
                      </div>
                      <button
                        className={connected ? 'ghost-button' : 'primary-button'}
                        type="button"
                        onClick={() => onConnectMercadoPago?.(team.id)}
                        disabled={!marketplace?.configured || connectingMarketplace}
                      >
                        {connected ? 'Reconectar' : 'Conectar'}
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="empty-state">Nenhum time disponível para conectar.</div>
            )}
          </>
        ) : (
          <div className="empty-state">Seu perfil pode visualizar a loja, mas não pode conectar contas de pagamento.</div>
        )}
      </SectionCard>

      <SectionCard title={form.id ? 'Editar produto' : 'Novo produto'} subtitle="Catálogo administrável com criação, atualização e exclusão lógica.">
        {canManage ? (
        <form className="admin-form" onSubmit={submit}>
          <div className="callout-box compact-box">
            <span className="eyebrow">Loja</span>
            <strong>{form.id ? 'Editando produto selecionado' : 'Criando novo produto'}</strong>
            <p>Preencha os dados do item e use o botão de excluir na tabela quando quiser remover da plataforma.</p>
          </div>
          <label>
            Nome
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          </label>
          <label>
            SKU
            <input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} required />
          </label>
          <div className="form-row">
            <label>
              Categoria
              <select value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })}>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>{category.name}</option>
                ))}
              </select>
            </label>
            <label>
              Time
              <select value={form.team_id} onChange={(e) => setForm({ ...form, team_id: e.target.value })}>
                <option value="">Sem vínculo</option>
                {teams.map((team) => (
                  <option key={team.id} value={team.id}>{team.name}</option>
                ))}
              </select>
            </label>
          </div>
          <div className="form-row">
            <label>
              Preço
              <input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} required />
            </label>
            <label>
              Estoque
              <input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} required />
            </label>
          </div>
          <div className="form-row">
            <label>
              Status
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                <option value="active">Ativo</option>
                <option value="inactive">Inativo</option>
                <option value="draft">Rascunho</option>
              </select>
            </label>
          </div>
          <div className="media-uploader">
            <div className="media-preview product-preview">
              {form.cover_image_url ? <img src={form.cover_image_url} alt={`Imagem de ${form.name || 'produto'}`} /> : <span>Sem imagem</span>}
            </div>
            <div className="media-uploader__controls">
              <label>
                Imagem do produto
                <input type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => setSelectedImage(event.target.files?.[0] || null)} />
              </label>
              <div className="inline-actions">
                <button className="ghost-button" type="button" onClick={uploadSelectedImage} disabled={!selectedImage || uploading}>
                  {uploading ? 'Enviando...' : 'Enviar imagem'}
                </button>
                <button className="inline-button" type="button" onClick={() => { setSelectedImage(null); setForm({ ...form, cover_image_url: '' }); setImageMessage('Imagem removida. Salve o produto para aplicar.'); }} disabled={!form.cover_image_url && !selectedImage}>
                  Remover imagem
                </button>
              </div>
              <small>JPG, PNG ou WEBP até 5 MB. Evita depender de link externo.</small>
              {imageMessage ? <small className="success-note compact">{imageMessage}</small> : null}
            </div>
          </div>
          <label>
            Descrição
            <textarea rows="4" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </label>
          <div className="inline-actions">
            <button className="primary-button" type="submit" disabled={creating || updating}>
              {form.id ? (updating ? 'Atualizando...' : 'Salvar produto') : (creating ? 'Salvando...' : 'Criar produto')}
            </button>
            {form.id ? <button className="inline-button" type="button" onClick={resetForm}>Cancelar edição</button> : null}
            {deleting ? <span className="eyebrow">Excluindo item...</span> : null}
          </div>
        </form>
        ) : (
          <div className="empty-state">Seu perfil pode visualizar a loja, mas não pode criar, editar ou excluir produtos.</div>
        )}
      </SectionCard>
    </div>
  );
}
